import { TrendingUp, IndianRupee, DollarSign, Coins, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/ui/components/primitives/alert";
import { Badge } from "@/ui/components/primitives/badge";
import { getWallets } from "@/services/api/services/payouts";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";

export function WalletPage() {
  const [wallets, setWallets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const wlts = await getWallets();
        setWallets(Array.isArray(wlts) ? wlts : []);
      } catch (e: any) {
        setError("Failed to load wallets");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatAmount = (n: number, c: string) => {
    if (c === "INR") return `₹${(n ?? 0).toLocaleString()}`;
    if (c === "USD") return `$${(n ?? 0).toLocaleString()}`;
    return `${(n ?? 0).toLocaleString()} ${c}`;
  };

  const CurrencyIcon = ({ currency }: { currency: string }) => {
    const className = "h-4 w-4";
    if (currency === "INR") return <IndianRupee className={className} strokeWidth={1.5} />;
    if (currency === "USD") return <DollarSign className={className} strokeWidth={1.5} />;
    return <Coins className={className} strokeWidth={1.5} />;
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1>Wallets</h1>
        <p className="text-muted-foreground mt-1">Your merchant wallets and balances</p>
      </div>

      {/* Wallet Cards */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading wallets…</p>
      ) : error ? (
        <Alert>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {wallets.length === 0 ? (
            <Card className="p-6">
              <p className="text-sm text-muted-foreground">No wallets found</p>
            </Card>
          ) : (
            wallets.map((w) => (
              <Card key={w.id} className={`p-6 ${w.currency === "INR" ? "border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10" : ""}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <CurrencyIcon currency={w.currency} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">{w.currency} Wallet</p>
                        <Badge variant="secondary" className="rounded-full">{w.currency}</Badge>
                      </div>
                      <p className="text-3xl font-semibold truncate">{formatAmount(w.availableBalance, w.currency)}</p>
                    </div>
                  </div>
                  <div className="h-10 w-10 rounded-lg bg-muted/50 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={w.isActive ? "default" : "secondary"}>{w.isActive ? "Active" : "Inactive"}</Badge>
                  <span className="text-xs text-muted-foreground">Last txn: {w.lastTransactionAt ? new Date(w.lastTransactionAt).toLocaleString() : "-"}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Reserved</p>
                    <p className="font-medium">{formatAmount(w.reserved, w.currency)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total</p>
                    <p className="font-medium">{formatAmount(w.balance, w.currency)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Wallet ID</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-xs truncate" title={w.id}>{w.id}</p>
                      <Button variant="outline" size="icon" aria-label="Copy Wallet ID" onClick={() => navigator.clipboard.writeText(w.id)}>
                        <Copy className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      
    </div>
  );
}
